import { useEffect, useState } from "react";
import { XIcon } from "@heroicons/react/outline";

const PromoterFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  isEdit = false,
  initialData = null,
}) => {
  const [form, setForm] = useState({
    name: "",
    company_name: "",
    company_id: "",
    company_reg_no: "",
    company_vat_no: "",
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    closestAirports: [],
    airportInput: "",
    addressLine1: "",
    addressLine2: "",
    addressLine3: "",
    city: "",
    county: "",
    country: "",
    postcode: "",
    venue_name: "",
    venue_addressLine1: "",
    venue_addressLine2: "",
    venue_addressLine3: "",
    venue_city: "",
    venue_county: "",
    venue_country: "",
    venue_postcode: "",
  });

  useEffect(() => {
    if (isEdit && initialData) {
      setForm({
        name: initialData.name || "",
        company_name: initialData.company_name || "",
        company_id: initialData.company_id || "",
        company_reg_no: initialData.company_reg_no || "",
        company_vat_no: initialData.company_vat_no || "",
        contact_name: initialData.contact_name || "",
        contact_phone: initialData.contact_phone || "",
        contact_email: initialData.contact_email || "",
        closestAirports: initialData.closest_airports?.map((a) => a.name) || [],
        airportInput: "",
        addressLine1: initialData.address?.address_1 || "",
        addressLine2: initialData.address?.address_2 || "",
        addressLine3: initialData.address?.address_3 || "",
        city: initialData.address?.city || "",
        county: initialData.address?.county || "",
        country: initialData.address?.country || "",
        postcode: initialData.address?.postcode || "",
        venue_name: initialData.venue?.name || "",
        venue_addressLine1: initialData.venue?.address.address_1 || "",
        venue_addressLine2: initialData.venue?.address.address_2 || "",
        venue_addressLine3: initialData.venue?.address.address_3 || "",
        venue_city: initialData.venue?.venue_city || "",
        venue_county: initialData.venue?.venue_county || "",
        venue_country: initialData.venue?.venue_country || "",
        venue_postcode: initialData.venue?.venue_postcode || "",
      });
    }

    if (!isEdit && isOpen) {
      // reset for create
      setForm((prev) => ({
        ...prev,
        name: "",
        company_name: "",
        company_id: "",
        company_reg_no: "",
        company_vat_no: "",
        contact_name: "",
        contact_phone: "",
        contact_email: "",
        closestAirports: [],
        airportInput: "",
        addressLine1: "",
        addressLine2: "",
        addressLine3: "",
        city: "",
        county: "",
        country: "",
        postcode: "",
        venue_name: "",
        venue_addressLine1: "",
        venue_addressLine2: "",
        venue_addressLine3: "",
        venue_city: "",
        venue_county: "",
        venue_country: "",
        venue_postcode: "",
      }));
    }
  }, [isEdit, initialData, isOpen]);

  if (!isOpen) return null;

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    onSubmit(form);
  };

  const handleAirportKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();

      const value = form.airportInput.trim();
      if (!value) return;

      if (!form.closestAirports.includes(value)) {
        setForm((prev) => ({
          ...prev,
          closestAirports: [...prev.closestAirports, value],
          airportInput: "",
        }));
      }
    }
  };

  const removeAirport = (name) => {
    setForm((prev) => ({
      ...prev,
      closestAirports: prev.closestAirports.filter((a) => a !== name),
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-2xl bg-slate-900 border border-white/10 rounded-xl shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white">
            {isEdit ? "Edit Promoter" : "Add Promoter"}
          </h2>

          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Basic Info */}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Promoter Name
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => handleChange("name", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Company Name
              </label>
              <input
                type="text"
                value={form.company_name}
                onChange={(e) => handleChange("company_name", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Company Id
              </label>
              <input
                type="text"
                value={form.company_id}
                onChange={(e) => handleChange("company_id", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Company Registration No
              </label>
              <input
                type="text"
                value={form.company_reg_no}
                onChange={(e) => handleChange("company_reg_no", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Company VAT No
              </label>
              <input
                type="text"
                value={form.company_vat_no}
                onChange={(e) => handleChange("company_vat_no", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Contact Name
              </label>
              <input
                type="text"
                value={form.contact_name}
                onChange={(e) => handleChange("contact_name", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Contact Number
              </label>
              <input
                type="text"
                value={form.contact_phone}
                onChange={(e) => handleChange("contact_phone", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Contact Email
              </label>
              <input
                type="email"
                value={form.contact_email}
                onChange={(e) => handleChange("contact_email", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>

            {/* Closest Airports */}
            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Closest Airports
              </label>

              {/* Input */}
              <input
                type="text"
                placeholder="Type airport name and press Enter"
                value={form.airportInput}
                onChange={(e) => handleChange("airportInput", e.target.value)}
                onKeyDown={handleAirportKeyDown}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mt-3">
                {form.closestAirports.map((airport) => (
                  <span
                    key={airport}
                    className="flex items-center gap-2 bg-slate-800 text-slate-200 px-3 py-1 rounded-full text-xs"
                  >
                    {airport}
                    <button
                      type="button"
                      onClick={() => removeAirport(airport)}
                      className="text-slate-400 hover:text-white"
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Venue */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-3">
              Venue Details
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                placeholder="Venue Name"
                value={form.venue_name}
                onChange={(e) => handleChange("venue_name", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="Address Line 1"
                value={form.venue_addressLine1}
                onChange={(e) =>
                  handleChange("venue_addressLine1", e.target.value)
                }
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="Address Line 2"
                value={form.venue_addressLine2}
                onChange={(e) =>
                  handleChange("venue_addressLine2", e.target.value)
                }
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="Address Line 3"
                value={form.venue_addressLine3}
                onChange={(e) =>
                  handleChange("venue_addressLine3", e.target.value)
                }
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="City"
                value={form.venue_city}
                onChange={(e) => handleChange("venue_city", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="County"
                value={form.venue_county}
                onChange={(e) => handleChange("venue_county", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="Country"
                value={form.venue_country}
                onChange={(e) => handleChange("venue_country", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="Postcode"
                value={form.venue_postcode}
                onChange={(e) => handleChange("venue_postcode", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>
          </div>

          {/* Address */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-3">Address</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                placeholder="Address Line 1"
                value={form.addressLine1}
                onChange={(e) => handleChange("addressLine1", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="Address Line 2"
                value={form.addressLine2}
                onChange={(e) => handleChange("addressLine2", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="Address Line 3"
                value={form.addressLine3}
                onChange={(e) => handleChange("addressLine3", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="City"
                value={form.city}
                onChange={(e) => handleChange("city", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="County"
                value={form.county}
                onChange={(e) => handleChange("county", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="Country"
                value={form.country}
                onChange={(e) => handleChange("country", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="Postcode"
                value={form.postcode}
                onChange={(e) => handleChange("postcode", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800 transition"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`px-4 py-2 text-sm rounded-lg text-white font-medium transition
              ${
                loading
                  ? "bg-pink-400 cursor-not-allowed"
                  : "bg-pink-600 hover:bg-pink-700"
              }
            `}
          >
            {loading
              ? "Saving..."
              : isEdit
              ? "Update Promoter"
              : "Create Promoter"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromoterFormModal;
